import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import CategorieWebSub from "../modificaProdotti/moduliArticolo/CategorieWebSub";
import { CreazioneKitContent } from "../modificaProdotti/CreazioneKit";
import ImportMassivoImmagini from "../modificaProdotti/ImportMassivoImmagini";
import { ModaArticoliStatsContent } from "./ModaArticoliStatsModal";
import { ModaArticoliExportContent } from "./ModaArticoliExportModal";

const normalizeCategoryId = (value) => {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildLabelMap = (nodes, map = new Map()) => {
  if (!Array.isArray(nodes)) {
    return map;
  }
  nodes.forEach((node) => {
    if (!node) {
      return;
    }
    const normalizedId = normalizeCategoryId(node.value);
    if (normalizedId !== null) {
      map.set(normalizedId, node.label ?? String(normalizedId));
    }
    if (Array.isArray(node.children) && node.children.length) {
      buildLabelMap(node.children, map);
    }
  });
  return map;
};

const parseSingleCategoryFilter = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => normalizeCategoryId(item))
    .filter((item) => item !== null);
};

const buildSiteCategoryTree = (categories = []) => {
  const nodesById = new Map();
  const parentById = new Map();

  (Array.isArray(categories) ? categories : []).forEach((category) => {
    const value = normalizeCategoryId(
      category?.external_id ?? category?.externalCategoryId
    );
    if (value === null || Number(category?.active) === 0) {
      return;
    }
    nodesById.set(value, {
      value,
      label: `${value} - ${category?.label || category?.path || value}`,
      children: [],
    });
    parentById.set(
      value,
      normalizeCategoryId(
        category?.parent_external_id ?? category?.parentExternalId
      )
    );
  });

  const roots = [];
  nodesById.forEach((node, value) => {
    const parent = nodesById.get(parentById.get(value));
    if (parent && parent !== node) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes) => {
    nodes.sort((left, right) => left.label.localeCompare(right.label, "it"));
    nodes.forEach((node) => sortNodes(node.children));
    return nodes;
  };

  return sortNodes(roots);
};

const pickValue = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) {
      return candidate;
    }
  }
  return undefined;
};

const BULK_JOB_TERMINAL_STATUSES = new Set([
  "SUCCESS",
  "PARTIAL_SUCCESS",
  "ERROR",
]);

const BULK_JOB_STATUS_LABELS = {
  PENDING: "In coda",
  RUNNING: "In esecuzione",
  SUCCESS: "Completato",
  PARTIAL_SUCCESS: "Completato con errori",
  ERROR: "Errore",
};

const DEFAULT_CATEGORY_MULTI_FILTER_ERROR =
  "Stai visualizzando articoli filtrati su più di una categoria. Alcuni di questi articoli potrebbero quindi non essere associati alla categoria che vuoi impostare come cat. di default. Perfavore, filtra gli articoli solamente sulla categoria che vuoi assegnare come cat. di default e torna qui.";

const getInitialSectionFeedback = () => ({
  saving: false,
  error: "",
  job: null,
});

const BULK_JOB_POLL_INTERVAL_MS = 3000;

function ModaArticoliFunctionsModal({
  open,
  onClose,
  totalRows = 0,
  token,
  utente,
  datiUlterioriStandard,
  siteCategories,
  selectionFilters,
  sortBy,
  sortDirection,
  stats,
  statsLoading,
  statsError,
  onRefreshStats,
  onRefresh,
  temaColori = "rootTema",
}) {
  const [categorieSelezionatePerSito, setCategorieSelezionatePerSito] =
    useState({ VIG: [], BOT: [] });
  const [sitoCategorieAttivo, setSitoCategorieAttivo] = useState("VIG");
  const [activeSection, setActiveSection] = useState("");
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState([]);
  const [sectionFeedback, setSectionFeedback] = useState({});
  const [alignmentDirection, setAlignmentDirection] = useState("VIG_TO_BOT");
  const [alignmentConfirmed, setAlignmentConfirmed] = useState(false);
  const [alignmentFeedback, setAlignmentFeedback] = useState({
    saving: false, error: "", result: null,
  });
  const [ecommerceFlags, setEcommerceFlags] = useState({
    attivoSuEcom: "",
    exportAmazon: "",
  });
  const [syncEcommerceOnline, setSyncEcommerceOnline] = useState(false);
  const [ecommerceFlagsConfirmed, setEcommerceFlagsConfirmed] = useState(false);
  const [forceSyncSite, setForceSyncSite] = useState("");
  const [forceSyncConfirmed, setForceSyncConfirmed] = useState(false);
  const canWriteDebug = Boolean(utente?.permessiAPI?.["write:debug"]);
  const canUpdatePrestashop = Boolean(
    utente?.permessiAPI?.["update:prestashop"]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setCategorieSelezionatePerSito({ VIG: [], BOT: [] });
    setSitoCategorieAttivo("VIG");
    setActiveSection("");
    setCopyLoading(false);
    setCopyError("");
    setCopySuccess(false);
    setCopiedCodes([]);
    setAlignmentDirection("VIG_TO_BOT");
    setAlignmentConfirmed(false);
    setAlignmentFeedback({ saving: false, error: "", result: null });
    setEcommerceFlags({ attivoSuEcom: "", exportAmazon: "" });
    setSyncEcommerceOnline(false);
    setEcommerceFlagsConfirmed(false);
    setForceSyncSite("");
    setForceSyncConfirmed(false);
  }, [open]);

  useEffect(() => {
    setForceSyncConfirmed(false);
  }, [selectionFilters, totalRows]);

  useEffect(() => {
    if (activeSection === "stats") {
      onRefreshStats?.();
    }
  }, [activeSection, onRefreshStats]);

  const categorieWeb = useMemo(() => {
    return Array.isArray(datiUlterioriStandard?.categorieWeb)
      ? datiUlterioriStandard.categorieWeb
      : [];
  }, [datiUlterioriStandard]);
  const categorieWebPerSito = useMemo(() => {
    const vigTree = buildSiteCategoryTree(siteCategories?.VIG);
    return {
      VIG: vigTree.length ? vigTree : categorieWeb,
      BOT: buildSiteCategoryTree(siteCategories?.BOT),
    };
  }, [categorieWeb, siteCategories]);
  const labelMap = useMemo(() => buildLabelMap(categorieWeb), [categorieWeb]);
  const alwaysOnValueIds = useMemo(() => {
    const values = Array.isArray(datiUlterioriStandard?.web?.alwaysOn?.dati)
      ? datiUlterioriStandard.web.alwaysOn.dati
      : [];
    const findId = (label) => {
      const match = values.find((item) => {
        return String(item?.desc || "").trim().toUpperCase() === label;
      });
      return normalizeCategoryId(match?.idPS);
    };
    return {
      yesId: findId("SI"),
      noId: findId("NO"),
    };
  }, [datiUlterioriStandard]);

  const filteredCategoryIds = useMemo(() => {
    const mongoFilters =
      selectionFilters && typeof selectionFilters === "object"
        ? selectionFilters.mongoFilters
        : null;
    const categoriaPredefinitaIn = parseSingleCategoryFilter(
      mongoFilters?.categoriaPredefinita_in
    );
    const categorieWebIn = parseSingleCategoryFilter(
      mongoFilters?.categorieWeb_in
    );
    return Array.from(
      new Set([...categoriaPredefinitaIn, ...categorieWebIn])
    );
  }, [selectionFilters]);
  const singleFilteredCategoryId =
    filteredCategoryIds.length === 1 ? Number(filteredCategoryIds[0]) : null;

  const defaultCategoryFilterState = useMemo(() => {
    if (!filteredCategoryIds.length) {
      return {
        ok: false,
        tone: "warning",
        error:
          "Per cambiare la categoria predefinita applica prima un filtro IN su 'Categoria web predefinita' oppure su 'Categorie web' nella tab Pubblicazione.",
      };
    }

    if (filteredCategoryIds.length > 1) {
      return {
        ok: false,
        tone: "error",
        error: DEFAULT_CATEGORY_MULTI_FILTER_ERROR,
      };
    }

    return {
      ok: true,
      tone: "success",
      categoryId: singleFilteredCategoryId,
      categoryLabel:
        labelMap.get(Number(singleFilteredCategoryId)) ??
        String(singleFilteredCategoryId),
    };
  }, [filteredCategoryIds, labelMap, singleFilteredCategoryId]);

  const handleKitSuccess = () => {
    setActiveSection("");
    onRefresh?.();
  };

  const copyToClipboard = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (!ok) {
      throw new Error("Impossibile copiare negli appunti.");
    }
  };

  const handleChangeCategorieWeb = (currentNode, selectedNodes) => {
    setCategorieSelezionatePerSito((prev) => ({
      ...prev,
      [sitoCategorieAttivo]: Array.isArray(selectedNodes) ? selectedNodes : [],
    }));
  };

  const totalOverLimit = totalRows > 20000;

  const getSectionFeedback = (sectionKey) => {
    return sectionFeedback[sectionKey] || getInitialSectionFeedback();
  };

  const updateSectionFeedback = (sectionKey, patch) => {
    setSectionFeedback((prev) => {
      const current = prev[sectionKey] || getInitialSectionFeedback();
      const nextPatch = typeof patch === "function" ? patch(current) : patch;
      return {
        ...prev,
        [sectionKey]: {
          ...current,
          ...nextPatch,
        },
      };
    });
  };

  const fetchCodiciFromFilters = async () => {
    return fetchCodiciFromCustomFilters(selectionFilters);
  };

  const fetchCodiciFromCustomFilters = async (filters) => {
    const response = await fetch(`${apiUrl}/moda/articoli/export`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filters,
        sortBy,
        sortDirection,
      }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error || "Impossibile recuperare i dati.");
    }
    const payload = await response.json();
    const rows = payload?.data ?? payload?.items ?? [];
    if (!Array.isArray(rows) || rows.length === 0) {
      throw new Error("Nessun dato da elaborare.");
    }
    const codici = rows
      .map((row) =>
        pickValue(
          row.codVS,
          row.cod_vs,
          row.codiceVS,
          row.codice_vs,
          row.codicevs
        )
      )
      .map((value) => (value === null || value === undefined ? "" : value))
      .map((value) => String(value).trim())
      .filter((value) => value.length >= 4);
    const uniqueCodici = Array.from(new Set(codici));
    if (!uniqueCodici.length) {
      throw new Error("Nessun codice VS valido trovato.");
    }
    return uniqueCodici;
  };

  const handleCopyCodes = async () => {
    if (!token) {
      setCopyError("Token non disponibile.");
      setCopySuccess(false);
      return;
    }
    if (totalOverLimit) {
      setCopyError(
        "La selezione corrente supera i 20000 articoli. Restringi i filtri."
      );
      setCopySuccess(false);
      return;
    }
    setCopyLoading(true);
    setCopyError("");
    setCopySuccess(false);
    setCopiedCodes([]);
    try {
      const codici = await fetchCodiciFromFilters();
      await copyToClipboard(codici.join(","));
      setCopiedCodes(codici);
      setCopySuccess(true);
    } catch (err) {
      setCopyError(err?.message || "Errore durante la copia dei codici.");
    } finally {
      setCopyLoading(false);
    }
  };

  const handleCreateAlignmentBatch = async () => {
    if (!canWriteDebug) {
      setAlignmentFeedback({ saving: false, error: "Permesso write:debug richiesto.", result: null });
      return;
    }
    if (!token) {
      setAlignmentFeedback({ saving: false, error: "Token non disponibile.", result: null });
      return;
    }
    if (!utente?.username) {
      setAlignmentFeedback({ saving: false, error: "Username utente non disponibile per l audit.", result: null });
      return;
    }
    if (!alignmentConfirmed) {
      setAlignmentFeedback({ saving: false, error: "Conferma lo snapshot dei filtri prima di procedere.", result: null });
      return;
    }
    if (!totalRows) {
      setAlignmentFeedback({ saving: false, error: "Nessun articolo nei filtri correnti.", result: null });
      return;
    }
    if (totalRows > 20000) {
      setAlignmentFeedback({ saving: false, error: "La selezione supera 20000 articoli. Restringi i filtri.", result: null });
      return;
    }
    setAlignmentFeedback({ saving: true, error: "", result: null });
    try {
      const codVS = await fetchCodiciFromFilters();
      const response = await axios.post(
        apiUrl + "/moda/prestashop-alignment/runs/filtered-preview",
        {
          codVS, direction: alignmentDirection, filters: selectionFilters,
          requestedBy: utente.username,
        },
        { headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" } },
      );
      setAlignmentFeedback({ saving: false, error: "", result: response?.data?.data || null });
    } catch (err) {
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.error || err?.message || "Batch di allineamento non creato.";
      setAlignmentFeedback({
        saving: false, error: code ? message + " (" + code + ")" : message, result: null,
      });
    }
  };

  useEffect(() => {
    const jobsToPoll = Object.entries(sectionFeedback).filter(([, feedback]) => {
      return (
        feedback?.job?.jobId &&
        !BULK_JOB_TERMINAL_STATUSES.has(feedback.job.status)
      );
    });

    if (!jobsToPoll.length || !token) {
      return;
    }

    let cancelled = false;

    const poll = async () => {
      await Promise.all(
        jobsToPoll.map(async ([sectionKey, feedback]) => {
          try {
            const response = await axios.get(
              `${apiUrl}/moda/articoli/bulk-jobs/${feedback.job.jobId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (cancelled) {
              return;
            }

            const nextJob = response?.data?.job ?? null;
            const previousStatus = feedback?.job?.status;
            updateSectionFeedback(sectionKey, {
              saving: false,
              error: "",
              job: nextJob,
            });

            if (
              nextJob &&
              BULK_JOB_TERMINAL_STATUSES.has(nextJob.status) &&
              !BULK_JOB_TERMINAL_STATUSES.has(previousStatus)
            ) {
              onRefresh?.();
            }
          } catch (err) {
            if (cancelled) {
              return;
            }
            updateSectionFeedback(sectionKey, {
              saving: false,
              error:
                err?.response?.data?.error ||
                err?.message ||
                "Errore durante il recupero dello stato del job.",
            });
          }
        })
      );
    };

    poll();
    const interval = window.setInterval(poll, BULK_JOB_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [sectionFeedback, token, onRefresh]);

  const scheduleBulkJob = async (sectionKey, jobType, payload, validationFn) => {
    if (!token) {
      updateSectionFeedback(sectionKey, {
        saving: false,
        error: "Token non disponibile.",
      });
      return;
    }
    if (totalOverLimit) {
      updateSectionFeedback(sectionKey, {
        saving: false,
        error: "La selezione corrente supera i 20000 articoli. Restringi i filtri.",
      });
      return;
    }
    if (typeof validationFn === "function") {
      const validationError = validationFn();
      if (validationError) {
        updateSectionFeedback(sectionKey, {
          saving: false,
          error: validationError,
        });
        return;
      }
    }

    updateSectionFeedback(sectionKey, {
      saving: true,
      error: "",
    });

    try {
      const response = await axios.post(
        `${apiUrl}/moda/articoli/bulk-jobs`,
        {
          jobType,
          filters: selectionFilters,
          sortBy,
          sortDirection,
          payload,
          requestedBy: utente?.username,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateSectionFeedback(sectionKey, {
        saving: false,
        error: "",
        job: response?.data?.job ?? null,
      });
    } catch (err) {
      updateSectionFeedback(sectionKey, {
        saving: false,
        error:
          err?.response?.data?.error ||
          err?.message ||
          "Errore durante la creazione del job massivo.",
      });
    }
  };

  const handleBulkAddCategories = async () => {
    await scheduleBulkJob(
      "addCategories",
      "add_categories",
      {
        categoriesBySite: categorieSelezionatePerSito,
      },
      () => {
        if (
          !categorieSelezionatePerSito.VIG.length &&
          !categorieSelezionatePerSito.BOT.length
        ) {
          return "Seleziona almeno una categoria VIG o BOT da aggiungere.";
        }
        return "";
      }
    );
  };

  const handleBulkChangeDefaultCategory = async () => {
    await scheduleBulkJob(
      "defaultCategory",
      "change_default_category",
      {
        categoriaPredefinita: singleFilteredCategoryId,
      },
      () => {
        if (!defaultCategoryFilterState.ok) {
          return defaultCategoryFilterState.error;
        }
        if (!singleFilteredCategoryId) {
          return "Impossibile determinare la categoria predefinita dai filtri attivi.";
        }
        return "";
      }
    );
  };

  const handleAlwaysOnPool = async () => {
    await scheduleBulkJob(
      "alwaysOnPool",
      "always_on_pool",
      {},
      () => {
        if (!alwaysOnValueIds.yesId || !alwaysOnValueIds.noId) {
          return "Configurazione Always On non trovata nei dati ulteriori.";
        }
        return "";
      }
    );
  };

  const handleAlwaysOnAdd = async () => {
    await scheduleBulkJob(
      "alwaysOnAdd",
      "always_on_add",
      {},
      () => {
        if (!alwaysOnValueIds.yesId) {
          return "Configurazione Always On non trovata nei dati ulteriori.";
        }
        return "";
      }
    );
  };

  const handleSetEcommerceFlags = async () => {
    await scheduleBulkJob(
      "ecommerceFlags",
      "set_ecommerce_flags",
      {
        attivoSuEcom: ecommerceFlags.attivoSuEcom || null,
        exportAmazon: ecommerceFlags.exportAmazon || null,
        syncOnline: syncEcommerceOnline,
      },
      () => {
        if (!ecommerceFlags.attivoSuEcom && !ecommerceFlags.exportAmazon) {
          return "Scegli almeno un dato da modificare.";
        }
        if (syncEcommerceOnline && !canUpdatePrestashop) {
          return "Permesso update:prestashop richiesto per sincronizzare verso VIG e BOT.";
        }
        if (!ecommerceFlagsConfirmed) {
          return "Conferma la modifica degli articoli filtrati prima di procedere.";
        }
        return "";
      }
    );
  };

  const handleForcePrestashopSync = async () => {
    await scheduleBulkJob(
      "forcePrestashopSync",
      "force_prestashop_sync",
      { site: forceSyncSite },
      () => {
        if (!canUpdatePrestashop) {
          return "Permesso update:prestashop richiesto per forzare la sincronizzazione.";
        }
        if (forceSyncSite !== "VIG" && forceSyncSite !== "BOT") {
          return "Seleziona il sito VIG o BOT.";
        }
        if (!forceSyncConfirmed) {
          return "Conferma sito e snapshot completo dei filtri correnti prima di procedere.";
        }
        return "";
      }
    );
  };

  const { buttonClassesWhite, buttonClassesGreen } = stilePulsanti;
  const isOpen = open && typeof document !== "undefined";
  const renderJobFeedback = (sectionKey) => {
    const feedback = getSectionFeedback(sectionKey);
    const job = feedback.job;
    const statusLabel = job
      ? BULK_JOB_STATUS_LABELS[job.status] || job.status
      : null;
    const boxClass = !job
      ? ""
      : job.status === "ERROR"
      ? "bg-washed-red dark-red"
      : job.status === "PARTIAL_SUCCESS"
      ? "bg-washed-yellow dark-gray"
      : job.status === "SUCCESS"
      ? "bg-washed-green dark-green"
      : "bg-washed-blue dark-blue";

    return (
      <>
        {feedback.error && (
          <div className="mt3 pa2 br2 bg-washed-red dark-red">
            {feedback.error}
          </div>
        )}
        {job && (
          <div className={`mt3 pa2 br2 ${boxClass}`}>
            <div>
              Stato: <strong>{statusLabel}</strong>
            </div>
            <div>
              Job ID: <strong>{job.jobId}</strong>
            </div>
            <div>
              In snapshot: <strong>{job.totalItems}</strong>
            </div>
            <div>
              Processati: <strong>{job.processedCount}</strong>
            </div>
            <div>
              Aggiornati: <strong>{job.updatedCount}</strong>
            </div>
            <div>
              Saltati: <strong>{job.skippedCount}</strong>
            </div>
            <div>
              In dashboard: <strong>{job.dashboardCount}</strong>
            </div>
            <div>
              Errori: <strong>{job.errorCount}</strong>
            </div>
            {job.message && <div className="mt2">{job.message}</div>}
            {job.recentErrors?.length > 0 && (
              <div className="mt2">
                <div className="b mb1">Ultimi errori</div>
                <ul className="mt0 mb0 pl3">
                  {job.recentErrors.map((item, index) => (
                    <li key={`${item.codVS}-${index}`}>
                      <strong>{item.codVS}</strong>
                      {item.message ? ` - ${item.message}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const addCategoriesFeedback = getSectionFeedback("addCategories");
  const defaultCategoryFeedback = getSectionFeedback("defaultCategory");
  const alwaysOnPoolFeedback = getSectionFeedback("alwaysOnPool");
  const alwaysOnAddFeedback = getSectionFeedback("alwaysOnAdd");
  const ecommerceFlagsFeedback = getSectionFeedback("ecommerceFlags");
  const forceSyncFeedback = getSectionFeedback("forcePrestashopSync");

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className={`modal ${temaColori}`} onClick={onClose}>
      <div
        className="w-90 w-70-l center pa4 br3 shadow-4 sfondoBiancoTema relative flex flex-column"
        style={{ maxHeight: "85vh", overflow: "hidden" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="bn bg-transparent absolute top-1 right-1 pointer f3"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="mt0 mb3">Funzioni</h3>

        <div className="flex flex-column flex-row-l">
          <div className="w-100 w-third-l pr3-l pb3 pb0-l">
            <div className="b mb2">Azioni rapide</div>
            <div className="flex flex-column">
              <button
                type="button"
                className={`${
                  activeSection === "stats"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) => (prev === "stats" ? "" : "stats"))
                }
              >
                Statistiche sugli articoli selezionati
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "kit"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) => (prev === "kit" ? "" : "kit"))
                }
              >
                Crea kit
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "imageBulkImport"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "imageBulkImport" ? "" : "imageBulkImport"
                  )
                }
              >
                Import massivo immagini
              </button>
              <button
                type="button"
                className={(activeSection === "alignment"
                  ? buttonClassesGreen
                  : buttonClassesWhite) + " mb2"}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "alignment" ? "" : "alignment"
                  )
                }
              >
                Replica VIG/BOT
              </button>
              <button
                type="button"
                className={(activeSection === "ecommerceFlags"
                  ? buttonClassesGreen
                  : buttonClassesWhite) + " mb2"}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "ecommerceFlags" ? "" : "ecommerceFlags"
                  )
                }
              >
                Attiva/disattiva su PS o AMZ
              </button>
              <button
                type="button"
                className={(activeSection === "forcePrestashopSync"
                  ? buttonClassesGreen
                  : buttonClassesWhite) + " mb2"}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "forcePrestashopSync" ? "" : "forcePrestashopSync"
                  )
                }
              >
                Forza sync con P.S.
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "export"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) => (prev === "export" ? "" : "export"))
                }
              >
                Esporta csv articoli selezionati
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "copy"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() => {
                  if (activeSection === "copy") {
                    setActiveSection("");
                    return;
                  }
                  setActiveSection("copy");
                  handleCopyCodes();
                }}
              >
                Copia in clipboard codici VS degli articoli selezionati
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "addCategories"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "addCategories" ? "" : "addCategories"
                  )
                }
              >
                Aggiungi categorie ai prodotti filtrati
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "defaultCategory"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "defaultCategory" ? "" : "defaultCategory"
                  )
                }
              >
                Cambia categoria predefinita
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "alwaysOnPool"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                } mb2`}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "alwaysOnPool" ? "" : "alwaysOnPool"
                  )
                }
              >
                Rendi i filtrati l'INTERO pool Always On (escludi il resto)
              </button>
              <button
                type="button"
                className={`${
                  activeSection === "alwaysOnAdd"
                    ? buttonClassesGreen
                    : buttonClassesWhite
                }`}
                onClick={() =>
                  setActiveSection((prev) =>
                    prev === "alwaysOnAdd" ? "" : "alwaysOnAdd"
                  )
                }
              >
                Aggiungi i filtrati a Always on
              </button>
            </div>
          </div>

          <div
            className="w-100 w-two-thirds-l pl3-l pt3 pt0-l bl-l bordoColAlt"
            style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}
          >
            {!activeSection && (
              <div className="pa3 br2 bg-near-white gray">
                Seleziona un'azione a sinistra per visualizzare i dettagli.
              </div>
            )}

            {activeSection === "stats" && (
              <div>
                <div className="flex justify-end mb2">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <ModaArticoliStatsContent
                  stats={stats}
                  loading={statsLoading}
                  error={statsError}
                  onRefresh={onRefreshStats}
                />
              </div>
            )}

            {activeSection === "kit" && (
              <div>
                <div className="flex justify-end mb2">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <CreazioneKitContent
                  onClose={() => setActiveSection("")}
                  token={token}
                  onSuccess={handleKitSuccess}
                  embedded
                />
              </div>
            )}

            {activeSection === "imageBulkImport" && (
              <div>
                <div className="flex justify-end mb2">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <ImportMassivoImmagini token={token} embedded />
              </div>
            )}

            {activeSection === "alignment" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div>
                    <div className="b">Replica VIG/BOT</div>
                    <div className="f7 gray mt1">Crea una run con lo snapshot completo dei filtri correnti.</div>
                  </div>
                  <button type="button" className={buttonClassesWhite} onClick={() => setActiveSection("")}>
                    Chiudi sezione
                  </button>
                </div>

                <div className="pa3 br2 bg-near-white mb3">
                  <div className="f6 mb1"><strong>Articoli nei filtri:</strong> {totalRows.toLocaleString("it-IT")}</div>
                  <div className="f7 gray">La paginazione della tabella non viene considerata. Il batch salva i codici presenti adesso nei filtri.</div>
                </div>

                <fieldset className="bn pa0 ma0 mb3">
                  <legend className="b f6 mb2">Direzione</legend>
                  <label className="db pa3 mb2 ba br2 pointer">
                    <input
                      type="radio" name="alignmentDirection" value="VIG_TO_BOT"
                      checked={alignmentDirection === "VIG_TO_BOT"}
                      onChange={(event) => { setAlignmentDirection(event.target.value); setAlignmentConfirmed(false); }}
                      disabled={alignmentFeedback.saving}
                    />
                    <span className="ml2 b">VIG → BOT</span>
                    <small className="db ml4 mt1 gray">Sono eleggibili gli articoli con pubblicato = SI.</small>
                  </label>
                  <label className="db pa3 ba br2 pointer">
                    <input
                      type="radio" name="alignmentDirection" value="BOT_TO_VIG"
                      checked={alignmentDirection === "BOT_TO_VIG"}
                      onChange={(event) => { setAlignmentDirection(event.target.value); setAlignmentConfirmed(false); }}
                      disabled={alignmentFeedback.saving}
                    />
                    <span className="ml2 b">BOT → VIG / Domino</span>
                    <small className="db ml4 mt1 gray">Sono eleggibili gli articoli con pubblicato = NO o PROBLEMA.</small>
                  </label>
                </fieldset>

                {totalRows > 20000 && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione supera 20000 articoli. Restringi i filtri prima di creare il batch.
                  </div>
                )}

                <label className="flex items-start mb3">
                  <input
                    className="mt1" type="checkbox" checked={alignmentConfirmed}
                    onChange={(event) => setAlignmentConfirmed(event.target.checked)}
                    disabled={alignmentFeedback.saving || !totalRows || totalRows > 20000}
                  />
                  <span className="ml2 f6">Confermo direzione e snapshot dei filtri. La run esegue soltanto l analisi: approvazione e applicazione restano nella pagina Allineamento.</span>
                </label>

                {alignmentFeedback.error && (
                  <div role="alert" className="mb3 pa2 br2 bg-washed-red dark-red">{alignmentFeedback.error}</div>
                )}
                {alignmentFeedback.result && (
                  <div role="status" className="mb3 pa3 br2 bg-washed-green dark-green">
                    <div className="b">Run {alignmentFeedback.result.runId}</div>
                    <div className="mt1">
                      {alignmentFeedback.result.selectedCodVS?.length || 0} articoli accodati; {alignmentFeedback.result.excluded?.length || 0} esclusi.
                    </div>
                    {!alignmentFeedback.result.workerEnabled && (
                      <div className="mt2">Worker batch non attivo: la run resta in attesa.</div>
                    )}
                    {alignmentFeedback.result.excluded?.length > 0 && (
                      <details className="mt2">
                        <summary>Mostra articoli esclusi</summary>
                        <ul className="pl3 mb0">
                          {alignmentFeedback.result.excluded.slice(0, 100).map((entry) => (
                            <li key={entry.codVS}><strong>{entry.codVS}</strong>: {entry.message}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <a className="dib mt3 link underline dark-green" href="/allineamentoPrestashop">Apri Allineamento VIG/BOT</a>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button" className={buttonClassesGreen}
                    onClick={handleCreateAlignmentBatch}
                    disabled={!canWriteDebug || alignmentFeedback.saving || !alignmentConfirmed || !totalRows || totalRows > 20000}
                  >
                    {alignmentFeedback.saving ? "Creazione batch..." : "Crea batch di allineamento"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "ecommerceFlags" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">Attiva/disattiva su PS o AMZ</div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>

                <p className="mt0 mb2 f6 gray">
                  Modifica solo i dati selezionati su tutti gli articoli dei
                  filtri correnti. Gli articoli che hanno gia il valore scelto
                  restano intoccati.
                </p>
                <div className="mb3 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>

                <div className="mb3">
                  <label className="db mb1 b f6" htmlFor="ecommerce-attivo-su-ecom">
                    Attivo su e-commerce (PS)
                  </label>
                  <select
                    id="ecommerce-attivo-su-ecom"
                    className="w-100 pa2 ba br2"
                    value={ecommerceFlags.attivoSuEcom}
                    onChange={(event) => {
                      const value = event.target.value;
                      setEcommerceFlags((current) => ({
                        ...current,
                        attivoSuEcom: value,
                      }));
                      setEcommerceFlagsConfirmed(false);
                    }}
                    disabled={ecommerceFlagsFeedback.saving}
                  >
                    <option value="">Non modificare</option>
                    <option value="SI">SI</option>
                    <option value="NO">NO</option>
                  </select>
                </div>

                <div className="mb3">
                  <label className="db mb1 b f6" htmlFor="ecommerce-export-amazon">
                    Export Amazon (AMZ)
                  </label>
                  <select
                    id="ecommerce-export-amazon"
                    className="w-100 pa2 ba br2"
                    value={ecommerceFlags.exportAmazon}
                    onChange={(event) => {
                      const value = event.target.value;
                      setEcommerceFlags((current) => ({
                        ...current,
                        exportAmazon: value,
                      }));
                      setEcommerceFlagsConfirmed(false);
                    }}
                    disabled={ecommerceFlagsFeedback.saving}
                  >
                    <option value="">Non modificare</option>
                    <option value="SI">SI</option>
                    <option value="NO">NO</option>
                  </select>
                </div>

                <label className="flex items-start mb2">
                  <input
                    className="mt1"
                    type="checkbox"
                    checked={syncEcommerceOnline}
                    onChange={(event) => {
                      setSyncEcommerceOnline(event.target.checked);
                      setEcommerceFlagsConfirmed(false);
                    }}
                    disabled={
                      ecommerceFlagsFeedback.saving || !canUpdatePrestashop
                    }
                  />
                  <span className="ml2 f6">
                    Sincronizza online gli articoli modificati verso VIG e BOT
                  </span>
                </label>
                {!canUpdatePrestashop && (
                  <div className="mb3 f7 gray">
                    Per la sincronizzazione online serve il permesso
                    update:prestashop. La modifica MongoDB puo essere eseguita
                    senza sincronizzazione.
                  </div>
                )}

                <label className="flex items-start mb3">
                  <input
                    className="mt1"
                    type="checkbox"
                    checked={ecommerceFlagsConfirmed}
                    onChange={(event) =>
                      setEcommerceFlagsConfirmed(event.target.checked)
                    }
                    disabled={
                      ecommerceFlagsFeedback.saving ||
                      (!ecommerceFlags.attivoSuEcom &&
                        !ecommerceFlags.exportAmazon)
                    }
                  />
                  <span className="ml2 f6">
                    Confermo valori e snapshot completo dei filtri correnti
                  </span>
                </label>

                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}

                {renderJobFeedback("ecommerceFlags")}

                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleSetEcommerceFlags}
                    disabled={
                      ecommerceFlagsFeedback.saving ||
                      totalOverLimit ||
                      !ecommerceFlagsConfirmed ||
                      (!ecommerceFlags.attivoSuEcom &&
                        !ecommerceFlags.exportAmazon) ||
                      (syncEcommerceOnline && !canUpdatePrestashop)
                    }
                  >
                    {ecommerceFlagsFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia modifica"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "forcePrestashopSync" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">Forza sync con P.S.</div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>

                <p className="mt0 mb2 f6 gray">
                  Accoda in dashboard gli articoli filtrati per una
                  sincronizzazione completa: ogni articolo viene inviato per
                  intero al sito scelto. Non modifica i dati Domino.
                </p>
                <div className="mb3 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>

                <div className="mb3">
                  <label className="db mb1 b f6" htmlFor="force-sync-site">
                    Sito da sincronizzare
                  </label>
                  <select
                    id="force-sync-site"
                    className="w-100 pa2 ba br2"
                    value={forceSyncSite}
                    onChange={(event) => {
                      setForceSyncSite(event.target.value);
                      setForceSyncConfirmed(false);
                    }}
                    disabled={forceSyncFeedback.saving}
                    required
                  >
                    <option value="">Seleziona un sito</option>
                    <option value="VIG">VIG</option>
                    <option value="BOT">BOT</option>
                  </select>
                </div>

                {!canUpdatePrestashop && (
                  <div className="mb3 f7 gray">
                    Per forzare la sincronizzazione serve il permesso
                    update:prestashop.
                  </div>
                )}

                <label className="flex items-start mb3">
                  <input
                    className="mt1"
                    type="checkbox"
                    checked={forceSyncConfirmed}
                    onChange={(event) =>
                      setForceSyncConfirmed(event.target.checked)
                    }
                    disabled={forceSyncFeedback.saving || !forceSyncSite}
                  />
                  <span className="ml2 f6">
                    Confermo sito e snapshot completo dei filtri correnti
                  </span>
                </label>

                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}

                {renderJobFeedback("forcePrestashopSync")}

                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleForcePrestashopSync}
                    disabled={
                      forceSyncFeedback.saving ||
                      totalOverLimit ||
                      !canUpdatePrestashop ||
                      !forceSyncSite ||
                      !forceSyncConfirmed
                    }
                  >
                    {forceSyncFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia sincronizzazione"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "export" && (
              <div>
                <div className="flex justify-end mb2">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <div className="pa3 bg-white">
                  <ModaArticoliExportContent
                    token={token}
                    filters={selectionFilters}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    totalRows={totalRows}
                    embedded
                  />
                </div>
              </div>
            )}

            {activeSection === "copy" && (
              <div>
                <div className="flex justify-end mb2">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                {copySuccess && <div className="mb2 b green">codici copiati</div>}
                {copyError && (
                  <div className="mb2 pa2 br2 bg-washed-red dark-red">
                    {copyError}
                  </div>
                )}
                <button
                  type="button"
                  className={buttonClassesWhite}
                  onClick={handleCopyCodes}
                  disabled={copyLoading}
                >
                  {copyLoading ? "Copio..." : "Copia di nuovo"}
                </button>
                {copiedCodes.length > 0 && (
                  <textarea
                    readOnly
                    className="w-100 mt2 pa2 ba br2"
                    rows={6}
                    value={copiedCodes.join(",")}
                  />
                )}
              </div>
            )}

            {activeSection === "addCategories" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">Aggiungi categorie ai prodotti filtrati</div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <div className="flex mb2" role="tablist" aria-label="Sito categorie">
                  {[
                    { key: "VIG", label: "vigliettisport.com" },
                    { key: "BOT", label: "botteroski.com" },
                  ].map((site) => (
                    <button
                      key={site.key}
                      type="button"
                      role="tab"
                      aria-selected={sitoCategorieAttivo === site.key}
                      className={`${
                        sitoCategorieAttivo === site.key
                          ? buttonClassesGreen
                          : buttonClassesWhite
                      } ${site.key === "VIG" ? "mr2" : ""}`}
                      onClick={() => setSitoCategorieAttivo(site.key)}
                    >
                      {site.label} ({categorieSelezionatePerSito[site.key].length})
                    </button>
                  ))}
                </div>
                <div className="mb3 pa2 br2 bg-near-white f6 gray">
                  Le selezioni dei due siti sono indipendenti: puoi scegliere
                  categorie per un solo sito oppure per entrambi. Verrà creata
                  la sincronizzazione soltanto verso i siti che hanno almeno
                  una categoria selezionata.
                </div>
                <p className="mt0 mb2 f6 gray">
                  Verranno aggiunte solo le categorie mancanti a tutti gli
                  articoli che corrispondono ai filtri attivi, senza
                  considerare la paginazione.
                </p>
                <div className="mb2 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>
                <div className="mb3 f6">
                  <label className="flex items-center">
                    <input type="checkbox" checked readOnly />
                    <span className="pl2">
                      Sincronizza le modifiche online (attivo)
                    </span>
                  </label>
                </div>

                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera i 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}

                <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
                  {categorieWebPerSito[sitoCategorieAttivo].length ? (
                    <CategorieWebSub
                      key={sitoCategorieAttivo}
                      onChange={handleChangeCategorieWeb}
                      data={categorieWebPerSito[sitoCategorieAttivo]}
                      categorie={
                        categorieSelezionatePerSito[sitoCategorieAttivo]
                      }
                    />
                  ) : (
                    <div className="pa2 br2 bg-washed-yellow dark-gray">
                      Nessuna categoria disponibile per questo sito.
                    </div>
                  )}
                </div>

                {renderJobFeedback("addCategories")}

                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleBulkAddCategories}
                    disabled={addCategoriesFeedback.saving || totalOverLimit}
                  >
                    {addCategoriesFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia aggiunta categorie"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "defaultCategory" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">Cambia categoria predefinita</div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <p className="mt0 mb2 f6 gray">
                  La modifica viene applicata a tutti gli articoli che
                  corrispondono ai filtri attivi, senza considerare la
                  paginazione. Se la categoria scelta non e gia associata al
                  prodotto, viene aggiunta prima di diventare predefinita.
                </p>
                <div className="mb2 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>
                <div className="mb3 f6">
                  <label className="flex items-center">
                    <input type="checkbox" checked readOnly />
                    <span className="pl2">
                      Sincronizza le modifiche online (attivo)
                    </span>
                  </label>
                </div>

                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera i 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}

                {defaultCategoryFilterState.ok ? (
                  <div className="mb3 pa2 br2 bg-washed-green dark-green">
                    Stai per impostare come categoria predefinita di tutti gli
                    articoli filtrati:{" "}
                    <strong>{defaultCategoryFilterState.categoryLabel}</strong>
                  </div>
                ) : (
                  <div
                    className={`mb3 pa2 br2 ${
                      defaultCategoryFilterState.tone === "error"
                        ? "bg-washed-red dark-red"
                        : "bg-washed-yellow dark-gray"
                    }`}
                  >
                    {defaultCategoryFilterState.error}
                  </div>
                )}

                {renderJobFeedback("defaultCategory")}

                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleBulkChangeDefaultCategory}
                    disabled={
                      defaultCategoryFeedback.saving ||
                      totalOverLimit ||
                      !defaultCategoryFilterState.ok
                    }
                  >
                    {defaultCategoryFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia cambio categoria predefinita"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "alwaysOnPool" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">
                    Rendi i prodotti filtrati l'INTERO pool Always On (escludi il resto)
                  </div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <p className="mt0 mb2 f6 gray">
                  Gli articoli filtrati verranno impostati su Always on = SI.
                  Tutti gli altri articoli che al momento hanno Always on = SI
                  verranno impostati su NO. Solo i prodotti realmente cambiati
                  verranno messi in dashboard sincronizzazione.
                </p>
                <div className="mb2 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>
                <div className="mb3 f6">
                  <label className="flex items-center">
                    <input type="checkbox" checked readOnly />
                    <span className="pl2">
                      Sincronizza le modifiche online (attivo)
                    </span>
                  </label>
                </div>
                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera i 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}
                {renderJobFeedback("alwaysOnPool")}
                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleAlwaysOnPool}
                    disabled={alwaysOnPoolFeedback.saving || totalOverLimit}
                  >
                    {alwaysOnPoolFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia aggiornamento pool Always On"}
                  </button>
                </div>
              </div>
            )}

            {activeSection === "alwaysOnAdd" && (
              <div>
                <div className="flex justify-between items-center mb2">
                  <div className="b">Aggiungi i prodotti filtrati a Always on</div>
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={() => setActiveSection("")}
                  >
                    Chiudi sezione
                  </button>
                </div>
                <p className="mt0 mb2 f6 gray">
                  Imposta Always on = SI solo sui prodotti filtrati che non lo
                  hanno gia configurato su SI. Solo i prodotti realmente
                  cambiati verranno messi in dashboard sincronizzazione.
                </p>
                <div className="mb2 f6">
                  <strong>Articoli filtrati:</strong>{" "}
                  {totalRows ? totalRows.toLocaleString("it-IT") : "0"}
                </div>
                <div className="mb3 f6">
                  <label className="flex items-center">
                    <input type="checkbox" checked readOnly />
                    <span className="pl2">
                      Sincronizza le modifiche online (attivo)
                    </span>
                  </label>
                </div>
                {totalOverLimit && (
                  <div className="mb3 pa2 br2 bg-washed-red dark-red">
                    La selezione corrente supera i 20000 articoli. Restringi i
                    filtri prima di procedere.
                  </div>
                )}
                {renderJobFeedback("alwaysOnAdd")}
                <div className="mt3 flex flex-wrap justify-between items-center">
                  <button
                    type="button"
                    className={buttonClassesWhite}
                    onClick={onClose}
                  >
                    Chiudi
                  </button>
                  <button
                    type="button"
                    className={buttonClassesGreen}
                    onClick={handleAlwaysOnAdd}
                    disabled={alwaysOnAddFeedback.saving || totalOverLimit}
                  >
                    {alwaysOnAddFeedback.saving
                      ? "Sto accodando..."
                      : "Avvia aggiunta a Always On"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ModaArticoliFunctionsModal;
